import Validator from 'validatorjs';
import $mitigations from 'app/stores/mitigations/$mitigations';
import { isIpV4Valid, isIpV6Valid, isIpValid } from 'core/util/ip';

const defaultAlertKeyRules = 'required|ip|rtbhMeetsNexthopFamily';

Validator.register(
  'rtbhMeetsNexthopFamily',
  function rtbhMeetsNexthopFamily(value) {
    const { pairingID } = this.validator.input;
    if (!pairingID) {
      return true; // don't validate this until we've selected a mitigation platform and method
    }
    const [platformID, methodID] = pairingID.split('-');

    const pair = $mitigations.platformsCollection.getPlatformMethod({ platformID, methodID });
    if (!pair) {
      return false;
    }

    const mitigation_method_id = pair.method.get('id');
    if (!mitigation_method_id) {
      return false;
    }

    const method = $mitigations.methodsCollection.get(mitigation_method_id);
    if (!method) {
      return false;
    }
    if (/^rtbh$/.test(method.type)) {
      const { nextHopV4, nextHopV6 } = method.details;
      const ipValid = isIpValid(value, { returnAddress: true });

      if (ipValid) {
        return ipValid.v4 ? isIpV4Valid(nextHopV4) : isIpV6Valid(nextHopV6);
      }
      return false;
    }

    // not an RTBH method, so just return true
    return true;
  },
  "IP to mitigate must belong to the same address family as your selected RTBH mitigation method's configured nexthop."
);

Validator.register(
  'canBeUsedForManualMitigation',
  function canBeUsedForManualMitigation() {
    const { pairingID } = this.validator.input;

    if (!pairingID) {
      return false;
    }

    const methodID = pairingID.split('-')?.[1] || '';

    const mitigationMethod = $mitigations.methodsCollection.modelById[methodID];

    if (mitigationMethod) {
      return mitigationMethod.canBeUsedForManualMitigation;
    }

    return true;
  },
  'This method cannot be used for manual mitigations as it infers incompatible values.'
);

const fields = {
  pairingID: {
    label: 'Mitigation Method',
    rules: 'required|canBeUsedForManualMitigation'
  },

  alertKey: {
    label: 'IP/CIDR to Mitigate',
    rules: defaultAlertKeyRules,
    defaultRules: defaultAlertKeyRules,
    placeholder: '127.0.0.1/32',
    validateDebounceDurationMs: 500
  },

  srcPort: {
    label: 'Source Port to Mitigate',
    placeholder: '',
    rules: 'integer',
    transform: {
      out: (value) => parseInt(value)
    }
  },

  dstPort: {
    label: 'Destination Port to Mitigate',
    placeholder: '',
    rules: 'integer',
    transform: {
      out: (value) => parseInt(value)
    }
  },

  comment: {
    label: 'Comment'
  },

  'uiProperties.autoStopValue': {
    label: 'Time Before Auto Stop (TTL)',
    helpText: 'Set the TTL to 0 to disable auto stop.',
    defaultValue: 0,
    rules: 'integer|min:0',
    transform: {
      out: (value) => parseInt(value)
    }
  },
  'uiProperties.autoStopFactor': {
    label: 'Time Before Auto Stop (TTL) Unit',
    defaultValue: 1, // In minutes
    options: [
      { label: 'minutes', value: 1 },
      { label: 'hours', value: 60 },
      { label: 'days', value: 60 * 24 }
    ]
  },

  tenantID: {
    label: 'Tenant',
    helpText: 'Associate this manual mitigation with a tenant. The mitigation will show as read-only for the tenant.',
    placeholder: 'Select a tenant...',
    defaultValue: ''
  }
};

const options = { name: 'Manual Mitigation' };

export { fields, options };
